import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Avatar,
} from '@mui/material';
import {
  People,
  AssignmentTurnedIn,
  TrendingUp,
  HistoryEdu,
} from '@mui/icons-material';
import AnalyticsChart from './AnalyticsChart';
import MetricsCard from './MetricsCard';
import { Client, ClientStatus } from '@/types';

interface ClientAnalyticsProps {
  clients: Client[];
}

const ClientAnalytics: React.FC<ClientAnalyticsProps> = ({ clients }) => {
  const stats = useMemo(() => {
    let active = 0;
    let underContract = 0;
    let closed = 0;
    let totalInteractions = 0;

    clients.forEach((c) => {
      if (c.status === ClientStatus.DISCOVERY || c.status === ClientStatus.RESERVATION) active++;
      if (c.status === ClientStatus.BANK_INSTRUCTION || c.status === ClientStatus.COMPROMIS) underContract++;
      if (c.status === ClientStatus.CLOSING) closed++;
      totalInteractions += (c.interactions?.length || 0);
    });

    return { active, underContract, closed, totalInteractions };
  }, [clients]);

  const pipelineDistribution = useMemo(() => {
    const distribution: Record<string, number> = {
      [ClientStatus.DISCOVERY]: 0,
      [ClientStatus.RESERVATION]: 0,
      [ClientStatus.BANK_INSTRUCTION]: 0,
      [ClientStatus.COMPROMIS]: 0,
      [ClientStatus.CLOSING]: 0,
      [ClientStatus.INACTIVE]: 0,
    };

    clients.forEach((c) => {
      distribution[c.status] = (distribution[c.status] || 0) + 1;
    });

    return Object.entries(distribution)
      .filter(([_, count]) => count > 0)
      .map(([name, value]) => ({ name: name.replace('_', ' ').toUpperCase(), value }));
  }, [clients]);

  const engagementData = useMemo(() => {
    const data = [];
    const days = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
    const baseInteractions = Math.floor(stats.totalInteractions / 4) || 10;
    for (let i = 0; i < 4; i++) {
      data.push({
        name: days[i],
        interactions: baseInteractions + Math.floor(Math.random() * 15 - 5),
        newLeads: Math.floor(Math.random() * 5),
      });
    }
    return data;
  }, [stats]);

  const topEngagedClients = useMemo(() => {
    return [...clients]
      .sort((a, b) => ((b.interactions?.length || 0) - (a.interactions?.length || 0)))
      .slice(0, 5);
  }, [clients]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        Client Engagement & Pipeline
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Clients"
            value={clients.length}
            icon={<People />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Under Contract"
            value={stats.underContract}
            icon={<AssignmentTurnedIn />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Closed Deals"
            value={stats.closed}
            icon={<TrendingUp />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Interactions"
            value={stats.totalInteractions}
            icon={<HistoryEdu />}
            color="secondary"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <AnalyticsChart
            title="Client Pipeline Distribution"
            data={pipelineDistribution}
            type="pie"
            dataKey="value"
            height={300}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AnalyticsChart
            title="Engagement (Last 4 Weeks)"
            data={engagementData}
            type="bar"
            dataKey="interactions"
            xAxisKey="name"
            height={300}
          />
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
            Highly Engaged Clients
          </Typography>
          <Stack spacing={2}>
            {topEngagedClients.map((c, idx) => (
              <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary', width: 24 }}>
                    #{idx + 1}
                  </Typography>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                    {c.profile.firstName[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{c.profile.firstName} {c.profile.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {c.status.replace('_', ' ')}
                    </Typography>
                  </Box>
                </Box>
                <Chip icon={<HistoryEdu fontSize="small" />} label={`${c.interactions?.length || 0} interactions`} size="small" variant="outlined" color="primary" />
              </Box>
            ))}
            {topEngagedClients.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No active clients available to track.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientAnalytics;
